import type { Patrol } from '@local/common-lib';
import {
  combine,
  formatDurationBetween,
  shortAddress,
} from '@local/common-lib';
import {
  Email as CommsIcon,
  Reply as CommsReplyIcon,
  Stop as FinishIcon,
  Circle as NowIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import {
  LoadingButton,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  timelineOppositeContentClasses,
} from '@mui/lab';
import { Chip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { DateTime } from 'luxon';
import { type FC, Fragment } from 'react';
import { getTimeLineDot } from './dot.js';
import { Plate } from './plate.js';

interface Props {
  finishingIncidentId?: string;
  onFinishIncident?: (incident: Patrol.Incident) => void;
  patrol: Patrol.Type;
}

export const PatrolTimeline: FC<Props> = ({
  finishingIncidentId,
  onFinishIncident,
  patrol,
}) => {
  const incidents = [...(patrol.incidents ?? [])];
  incidents.sort((a, b) => b.when.getTime() - a.when.getTime());
  const latestIncident = incidents[0];

  return (
    <Timeline
      onPointerEnterCapture={() => {}}
      onPointerLeaveCapture={() => {}}
      placeholder={<Fragment />}
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.2,
        },
      }}
    >
      {patrol.finished ? (
        <TimelineItem>
          <TimelineOppositeContent
            align="right"
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
            variant="body2"
          >
            {DateTime.fromJSDate(patrol.finished.finished).toLocaleString(
              DateTime.TIME_SIMPLE,
            )}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="info">
              <FinishIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            <Typography component="span">
              Finished
              {patrol.started?.vehicle && patrol.finished?.vehicle
                ? ` - ${(
                    patrol.finished?.vehicle.finishOdometer -
                      patrol.started?.vehicle.startOdometer
                  ).toFixed(1)} km in ${patrol.started?.vehicle.rego}`
                : ''}
            </Typography>
            {patrol.finished.location?.address ? (
              <Typography color="text.secondary" variant="body2">
                {shortAddress(patrol.finished.location?.address)}
              </Typography>
            ) : undefined}
            {patrol.eccLogOffNotification ? (
              <Typography color="info.main" fontWeight="bold" variant="body2">
                ECC Log Off
              </Typography>
            ) : undefined}
          </TimelineContent>
        </TimelineItem>
      ) : (
        <TimelineItem>
          <TimelineOppositeContent
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
          >
            now
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot>
              <NowIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            Still ongoing
          </TimelineContent>
        </TimelineItem>
      )}
      {incidents.map((incident) => (
        <TimelineItem key={incident._id}>
          <TimelineOppositeContent
            align="right"
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
            variant="body2"
          >
            {DateTime.fromJSDate(incident.when).toLocaleString(
              DateTime.TIME_SIMPLE,
            )}
          </TimelineOppositeContent>
          <TimelineSeparator>
            {getTimeLineDot(incident.what)}
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            {incident.what !== 'Location' ? (
              <Typography component="span">{incident.what}</Typography>
            ) : null}
            {incident.whatDetails ? (
              <Typography color="text.secondary" variant="body2">
                {incident.whatDetails}
              </Typography>
            ) : undefined}
            {incident.whenFinished ? (
              <Typography color="text.secondary" variant="body2">
                Duration:{' '}
                {formatDurationBetween(incident.when, incident.whenFinished)}
              </Typography>
            ) : undefined}
            {incident.location?.address ? (
              <Typography color="text.secondary" variant="body2">
                {shortAddress(incident.location?.address)}
              </Typography>
            ) : undefined}
            {onFinishIncident &&
            latestIncident?._id === incident._id &&
            incident.what !== 'Location' &&
            !incident.whenFinished ? (
              <LoadingButton
                loading={finishingIncidentId === incident._id}
                onClick={() => onFinishIncident(incident)}
                size="small"
                startIcon={<FinishIcon />}
                sx={{ mt: 1 }}
                variant="outlined"
              >
                End incident
              </LoadingButton>
            ) : null}
            {incident.vehicles?.length
              ? incident.vehicles.map((vehicle) => (
                  <Stack
                    alignItems="center"
                    direction="row"
                    key={vehicle.rego}
                    p="2px"
                    spacing={2}
                  >
                    <Plate
                      rego={vehicle.rego}
                      stolen={!!vehicle.reportedStolen}
                    />
                    {vehicle.color ? (
                      <Typography variant="body2">
                        {combine(
                          ' ',
                          vehicle.color,
                          vehicle.make,
                          vehicle.model,
                        )}
                      </Typography>
                    ) : undefined}
                    {vehicle.reportedStolen ? (
                      <Chip color="error" label="Stolen" size="small" />
                    ) : undefined}
                  </Stack>
                ))
              : undefined}
          </TimelineContent>
        </TimelineItem>
      ))}
      {patrol.started ? (
        <TimelineItem>
          <TimelineOppositeContent
            align="right"
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
            variant="body2"
          >
            {DateTime.fromJSDate(patrol.started.started).toLocaleString(
              DateTime.TIME_SIMPLE,
            )}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="success">
              <StartIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            <Typography component="span">
              Started
              {patrol.started.policeEventNumber
                ? ` - ${patrol.started.policeEventNumber}`
                : ''}
            </Typography>
            {patrol.started.location?.address ? (
              <Typography color="text.secondary" variant="body2">
                {shortAddress(patrol.started.location?.address)}
              </Typography>
            ) : undefined}
          </TimelineContent>
        </TimelineItem>
      ) : undefined}
      {patrol.eccLogOnResponse ? (
        <TimelineItem>
          <TimelineOppositeContent
            align="right"
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
            variant="body2"
          >
            {DateTime.fromJSDate(patrol.eccLogOnResponse.when).toLocaleString(
              DateTime.TIME_SIMPLE,
            )}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="success">
              <CommsReplyIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            <Typography component="span">
              ECC Reply
              {patrol.eccLogOnResponse.policeEventNumber
                ? ` - ${patrol.eccLogOnResponse.policeEventNumber}`
                : ''}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ) : undefined}
      {patrol.eccLogOnNotification ? (
        <TimelineItem>
          <TimelineOppositeContent
            align="right"
            color="text.secondary"
            sx={{ m: 'auto 0', pb: 2 }}
            variant="body2"
          >
            {DateTime.fromJSDate(
              patrol.eccLogOnNotification.when,
            ).toLocaleString(DateTime.TIME_SIMPLE)}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="info">
              <CommsIcon />
            </TimelineDot>
          </TimelineSeparator>
          <TimelineContent sx={{ m: 'auto 0', pb: 2 }}>
            <Typography component="span">ECC Log On</Typography>
          </TimelineContent>
        </TimelineItem>
      ) : undefined}
    </Timeline>
  );
};
